import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircleIcon, ExclamationTriangleIcon, EyeIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import apiService from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { Company } from '../../types/auth';
import { Document } from './RegistrationWizard';

interface SummaryStepProps {
    company: Company | null;
    documents: Document[];
    onSubmit: () => void;
    isSubmitting: boolean;
}

export const SummaryStep: React.FC<SummaryStepProps> = ({
    company,
    documents,
    onSubmit,
    isSubmitting
}) => {
    const { user, refreshUser } = useAuth();
    const [isSubmittingForModeration, setIsSubmittingForModeration] = useState(false);
    const navigate = useNavigate();

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const handleViewDocument = async (document: Document) => {
        try {
            const response = await apiService.getDocumentUrl(document.id);
            if (response.data.success) {
                window.open((response.data as any).url, '_blank');
            } else {
                toast.error(response.data.message);
            }
        } catch (error: any) {
            console.error('Ошибка получения URL документа:', error);
            toast.error('Ошибка открытия документа');
        }
    };

    const handleSubmitForModeration = async () => {
        if (!company) {
            toast.error('Заполните информацию о компании');
            return;
        }

        const requiredDocuments = documents.filter(d =>
            ['DIRECTOR_APPOINTMENT_ORDER', 'COMPANY_CHARTER'].includes(d.type)
        );

        if (requiredDocuments.length < 2) {
            toast.error('Загрузите все обязательные документы');
            return;
        }

        setIsSubmittingForModeration(true);
        onSubmit();

        try {
            const response = await apiService.submitForModeration();
            if (response.data.success) {
                toast.success(response.data.message);
                await refreshUser();
                // Редирект на главную страницу (дашборд)
                navigate('/client/dashboard');
            } else {
                toast.error(response.data.message);
            }
        } catch (error: any) {
            console.error('Ошибка подачи заявки:', error);
            const message = error.response?.data?.message || 'Ошибка подачи заявки на модерацию';
            toast.error(message);
        } finally {
            setIsSubmittingForModeration(false);
        }
    };

    const handleResubmitApplication = async () => {
        setIsSubmittingForModeration(true);
        onSubmit();

        try {
            const response = await apiService.resubmitApplication();
            if (response.data.success) {
                toast.success(response.data.message);
                await refreshUser();
                // Редирект на главную страницу (дашборд)
                navigate('/client/dashboard');
            } else {
                toast.error(response.data.message);
            }
        } catch (error: any) {
            console.error('Ошибка повторной подачи заявки:', error);
            const message = error.response?.data?.message || 'Ошибка повторной подачи заявки';
            toast.error(message);
        } finally {
            setIsSubmittingForModeration(false);
        }
    };

    const getDocumentStatusBadge = (status: string) => {
        const statusConfig = {
            'APPROVED': { color: 'bg-green-100 text-green-800', text: 'Одобрен' },
            'REJECTED': { color: 'bg-red-100 text-red-800', text: 'Отклонен' },
            'NEEDS_REVISION': { color: 'bg-orange-100 text-orange-800', text: 'На доработке' },
            'PENDING_MODERATION': { color: 'bg-yellow-100 text-yellow-800', text: 'На модерации' }
        };

        const config = statusConfig[status as keyof typeof statusConfig] ||
            { color: 'bg-gray-100 text-gray-800', text: 'Неизвестно' };

        return (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
                {config.text}
            </span>
        );
    };

    const canSubmit = () => {
        if (!company) return false;

        const requiredDocuments = documents.filter(d =>
            ['DIRECTOR_APPOINTMENT_ORDER', 'COMPANY_CHARTER'].includes(d.type) &&
            d.status !== 'REJECTED'
        );

        return requiredDocuments.length >= 2;
    };

    return (
        <div className="space-y-8">
            {/* Информация о компании */}
            <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                    Информация о компании
                </h3>
                {company ? (
                    <div className="bg-gray-50 rounded-lg p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <p className="text-sm font-medium text-gray-500">БИН</p>
                                <p className="text-base text-gray-900">{company.bin}</p>
                            </div>
                            <div>
                                <p className="text-sm font-medium text-gray-500">Название</p>
                                <p className="text-base text-gray-900">{company.name}</p>
                            </div>
                            <div className="md:col-span-2">
                                <p className="text-sm font-medium text-gray-500">Юридический адрес</p>
                                <p className="text-base text-gray-900">{company.legalAddress}</p>
                            </div>
                            <div className="md:col-span-2">
                                <p className="text-sm font-medium text-gray-500">Фактический адрес</p>
                                <p className="text-base text-gray-900">{company.actualAddress}</p>
                            </div>
                            <div>
                                <p className="text-sm font-medium text-gray-500">Телефон</p>
                                <p className="text-base text-gray-900">{company.contactPhone}</p>
                            </div>
                            <div>
                                <p className="text-sm font-medium text-gray-500">Email</p>
                                <p className="text-base text-gray-900">{company.contactEmail}</p>
                            </div>
                            <div className="md:col-span-2">
                                <p className="text-sm font-medium text-gray-500">Директор</p>
                                <p className="text-base text-gray-900">{company.directorName}</p>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <div className="flex items-center">
                            <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mr-2" />
                            <span className="text-red-800">Информация о компании не заполнена</span>
                        </div>
                    </div>
                )}
            </div>

            {/* Документы */}
            <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    {documents.length > 0 ? (
                        <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                    ) : (
                        <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mr-2" />
                    )}
                    Документы ({documents.length})
                </h3>

                {documents.length > 0 ? (
                    <div className="space-y-3">
                        {documents.map((doc) => (
                            <div key={doc.id} className="bg-gray-50 rounded-lg p-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center space-x-3 mb-2">
                                            <h4 className="text-sm font-medium text-gray-900">
                                                {doc.typeDescription}
                                            </h4>
                                            {getDocumentStatusBadge(doc.status)}
                                        </div>
                                        <p className="text-sm text-gray-600 truncate">{doc.fileName}</p>
                                        <p className="text-xs text-gray-500">
                                            {formatFileSize(doc.fileSize)} • {new Date(doc.uploadedAt).toLocaleString('ru-RU')}
                                        </p>
                                        {doc.rejectionReason && (
                                            <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded">
                                                <p className="text-xs text-red-700">
                                                    <strong>Причина отклонения:</strong> {doc.rejectionReason}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                    <button
                                        onClick={() => handleViewDocument(doc)}
                                        className="ml-4 p-2 text-gray-400 hover:text-gray-600 transition-colors"
                                        title="Просмотреть документ"
                                    >
                                        <EyeIcon className="h-5 w-5" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <div className="flex items-center">
                            <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mr-2" />
                            <span className="text-red-800">Документы не загружены</span>
                        </div>
                    </div>
                )}
            </div>

            {/* Статус заявки */}
            {user?.status && user.status !== 'DRAFT' && (
                <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Статус заявки</h3>
                    <div className={`rounded-lg p-4 ${user.status === 'PENDING_MODERATION' ? 'bg-yellow-50 border border-yellow-200' :
                        user.status === 'APPROVED' ? 'bg-green-50 border border-green-200' :
                            user.status === 'NEEDS_REVISION' ? 'bg-orange-50 border border-orange-200' :
                                'bg-red-50 border border-red-200'
                        }`}>
                        <div className="flex items-center">
                            <div className={`h-3 w-3 rounded-full mr-3 ${user.status === 'PENDING_MODERATION' ? 'bg-yellow-400 animate-pulse' :
                                user.status === 'APPROVED' ? 'bg-green-400' :
                                    user.status === 'NEEDS_REVISION' ? 'bg-orange-400' :
                                        'bg-red-400'
                                }`}></div>
                            <div>
                                <h4 className={`font-medium ${user.status === 'PENDING_MODERATION' ? 'text-yellow-800' :
                                    user.status === 'APPROVED' ? 'text-green-800' :
                                        user.status === 'NEEDS_REVISION' ? 'text-orange-800' :
                                            'text-red-800'
                                    }`}>
                                    {user.status === 'PENDING_MODERATION' && 'Заявка на модерации'}
                                    {user.status === 'APPROVED' && 'Заявка одобрена'}
                                    {user.status === 'NEEDS_REVISION' && 'Требуется доработка'}
                                    {user.status === 'REJECTED' && 'Заявка отклонена'}
                                </h4>
                                <p className={`text-sm mt-1 ${user.status === 'PENDING_MODERATION' ? 'text-yellow-700' :
                                    user.status === 'APPROVED' ? 'text-green-700' :
                                        user.status === 'NEEDS_REVISION' ? 'text-orange-700' :
                                            'text-red-700'
                                    }`}>
                                    {user.status === 'PENDING_MODERATION' && 'Ваша заявка рассматривается модераторами'}
                                    {user.status === 'APPROVED' && 'Поздравляем! Теперь вы можете пользоваться системой'}
                                    {user.status === 'NEEDS_REVISION' && 'Внесите изменения и подайте заявку повторно'}
                                    {user.status === 'REJECTED' && 'Обратитесь в поддержку для получения информации'}
                                </p>
                            </div>
                        </div>

                        {user.rejectionReason && (
                            <div className="mt-3 p-3 bg-white rounded border">
                                <p className="text-sm font-medium text-gray-900 mb-1">Комментарий модератора:</p>
                                <p className="text-sm text-gray-700">{user.rejectionReason}</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Действия */}
            <div className="border-t border-gray-200 pt-6">
                {user?.status === 'DRAFT' && (
                    <div className="text-center">
                        {canSubmit() ? (
                            <div>
                                <button
                                    onClick={handleSubmitForModeration}
                                    disabled={isSubmittingForModeration}
                                    className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center mx-auto"
                                >
                                    {isSubmittingForModeration ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                            Подача заявки...
                                        </>
                                    ) : (
                                        <>
                                            <PaperAirplaneIcon className="h-4 w-4 mr-2" />
                                            Подать заявку на модерацию
                                        </>
                                    )}
                                </button>
                                <p className="text-sm text-gray-500 mt-2">
                                    После подачи заявка будет рассмотрена модераторами в течение 2-3 рабочих дней
                                </p>
                            </div>
                        ) : (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500 mx-auto mb-2" />
                                <p className="text-yellow-800 font-medium">Заявка не готова к подаче</p>
                                <p className="text-yellow-700 text-sm mt-1">
                                    Заполните информацию о компании и загрузите все обязательные документы
                                </p>
                            </div>
                        )}
                    </div>
                )}

                {user?.status === 'NEEDS_REVISION' && (
                    <div className="text-center">
                        <button
                            onClick={handleResubmitApplication}
                            disabled={isSubmittingForModeration || !canSubmit()}
                            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center mx-auto"
                        >
                            {isSubmittingForModeration ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                    Подача заявки...
                                </>
                            ) : (
                                <>
                                    <PaperAirplaneIcon className="h-4 w-4 mr-2" />
                                    Подать заявку повторно
                                </>
                            )}
                        </button>
                        <p className="text-sm text-gray-500 mt-2">
                            Убедитесь, что внесли все необходимые изменения
                        </p>
                    </div>
                )}

                {(user?.status === 'PENDING_MODERATION' || user?.status === 'APPROVED') && (
                    <div className="text-center">
                        <div className="inline-flex items-center px-4 py-2 bg-gray-100 rounded-lg">
                            <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                            <span className="text-gray-700">Заявка подана</span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
